import React, { useState, useCallback } from "react";
import { getSessionUser } from "../../utils/auth/sessionUser";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import {
	HomeOutlined,
	HeartOutlined,
	ExperimentOutlined,
	PartitionOutlined,
	AuditOutlined,
	ShopOutlined,
	SettingOutlined,
	UserOutlined,
	LogoutOutlined,
	QuestionOutlined,
	LockOutlined,
	TeamOutlined,
	LeftOutlined,
	RightOutlined,
	MenuOutlined,
	DownOutlined,
	MedicineBoxOutlined,
	SafetyCertificateOutlined,
	ReadOutlined,
	CalendarOutlined,
	CalendarFilled,
	MessageOutlined,
	FormOutlined,
	FileTextOutlined,
	InboxOutlined,
	ProfileOutlined,
	NodeIndexOutlined,
	FileDoneOutlined,
	CreditCardOutlined,
	SyncOutlined,
	DatabaseOutlined,
	ShoppingCartOutlined,
	ShoppingOutlined,
	ContactsOutlined,
	SafetyOutlined,
} from "@ant-design/icons";
import {
	canAccessResearch,
	canAccessXoe,
	canRaiseInvoices,
	canReviewHiAlerts,
	canUseGeneCodesWheel,
	canViewAppointments,
	canViewCommunications,
	canViewFormsInTray,
	canViewLabOrders,
	canViewLabProcessing,
	canViewLabTracking,
	canViewPatients,
	canViewPayments,
	canViewTestOrders,
	canViewTreatmentPlans,
	hasInventoryAccess,
	isPatientSession,
} from "../../utils/userPrefs/validatePermissions";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";
import { useXoeChatMode } from "../../utils/userPrefs/UserPrefs";
import { isCustomerDirectoryMember } from "../../utils/customer/customerDirectory";
import { CUSTOMER_PRODUCTS_PATH } from "../customer/customerDestinations";
import type { PatientDashboardSection, PatientSectionKey } from "../../pages/patient/dashboardSections";
import XoeMark from "../xoe/XoeMark";
import { useSettings } from "../settings/SettingsContext";
import { selectableSettingsTabs } from "../settings/settingsTabs";
import { useBranding } from "../../context/BrandingContext";
import BrandLogo from "../branding/BrandLogo";
import PoweredBy from "../branding/PoweredBy";
import NavItem from "./NavItem";
import PasswordResetModal from "./PasswordResetModal";
import { ENROLMENT_PATH } from "../../lib/http/stepup";
import "./Sidebar.css";

// ── Types ──────────────────────────────────────────────────────
interface NavSubItem {
	key: string;
	label: string;
	icon?: React.ReactNode;
}

interface NavGroupType {
	key: string;
	label: string;
	icon: React.ReactNode;
	children: NavSubItem[];
}


type NavEntry =
	| { kind: "heading"; label: string }
	| {
		kind: "item";
		path: string;
		label: string;
		icon: React.ReactNode;
		visible: boolean;
		/** Defaults to `isPathActive(path)`. Override only where that rule is wrong. */
		isActive?: (p: string) => boolean;
	}
	| { kind: "group"; group: NavGroupType; visible: boolean };


const isPathActive = (path: string) => (p: string) => p === path || p.startsWith(path + "/");

export interface PatientNavProps {
	/** Already filtered by `visiblePatientSections()`. Rail order == DOM order == scroll order. */
	sections: PatientDashboardSection[];
	/** Sparse by design: a section with nothing to count simply omits its key (see NavItem). */
	counts: Partial<Record<PatientSectionKey, number>>;
	onSectionSelect: (key: PatientSectionKey) => void;
	/** Reserved for the S6 scroll-spy. Consumed here only as the `isActive` predicate. */
	activeSectionKey?: PatientSectionKey;
	/** Wired by S5. Declared so the contract is stable; this file renders no entry for it. */
	onBookAppointment?: () => void;
}

interface SidebarProps {
	collapsed: boolean;
	onCollapse: (v: boolean) => void;
	patientNav?: PatientNavProps;
}

// ── Sub-item ───────────────────────────────────────────────────
const SubItem: React.FC<{
	item: NavSubItem;
	isActive: boolean;
	collapsed: boolean;
	onClick: () => void;
}> = ({ item, isActive, collapsed, onClick }) => {
	const el = (
		<div className={`mg-nav-sub-item${isActive ? " active" : ""}`} onClick={onClick} role="menuitem">
			<span className="mg-nav-sub-item-dot" />
			{item.label}
		</div>
	);
	if (collapsed) return null;
	return el;
};

// ── Nav group (collapsible section) ───────────────────────────
const NavGroup: React.FC<{
	group: NavGroupType;
	collapsed: boolean;
	activePath: string;
	onNavigate: (path: string) => void;
}> = ({ group, collapsed, activePath, onNavigate }) => {
	const activeChildKey = group.children
		.filter((c) => activePath === c.key || activePath.startsWith(c.key + "/"))
		.reduce<string | null>((best, c) => (best === null || c.key.length > best.length ? c.key : best), null);
	const hasActive = activeChildKey !== null;
	const [open, setOpen] = useState(hasActive);

	if (collapsed) {
		const subMenu: MenuProps["items"] = [
			{ key: "__label__", label: group.label, disabled: true, style: { cursor: "default", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.5, padding: "4px 12px", minHeight: 0 } },
			{ type: "divider" },
			...group.children.map((c) => ({
				key: c.key,
				label: c.label,
				icon: c.icon,
				onClick: () => onNavigate(c.key),
			})),
		];
		return (
			<Dropdown
				menu={{ items: subMenu }}
				//Ignore this error, the type definition for antd is incorrect for this prop
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				placement="rightTop"
				trigger={["hover"]}
				overlayStyle={{ marginLeft: 4 }}
			>
				<div className={`mg-nav-group-trigger${hasActive ? " has-active-child" : ""}`}>
					<span className="mg-nav-item-icon">{group.icon}</span>
				</div>
			</Dropdown>
		);
	}

	return (
		<div>
			<div className={`mg-nav-group-trigger${hasActive ? " has-active-child" : ""}`} onClick={() => setOpen((v) => !v)} role="button">
				<span className="mg-nav-item-icon">{group.icon}</span>
				<span className="mg-nav-item-label">{group.label}</span>
				<RightOutlined className={`mg-nav-group-chevron${open ? " open" : ""}`} />
			</div>
			<div className="mg-nav-sub" style={{ maxHeight: open ? group.children.length * 40 : 0 }}>
				{group.children.map((c) => (
					<SubItem key={c.key} item={c} isActive={activeChildKey === c.key} collapsed={collapsed} onClick={() => onNavigate(c.key)} />
				))}
			</div>
		</div>
	);
};


// ── Main Sidebar ───────────────────────────────────────────────
const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse, patientNav }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const user = getSessionUser();
	const activePath = location.pathname;
	const { branding, isResolved } = useBranding();

	const [mobileOpen, setMobileOpen] = useState(false);
	const [showPasswordReset, setShowPasswordReset] = useState(false);

	const go = useCallback(
		(path: string) => {
			navigate(path);
			setMobileOpen(false);
		},
		[navigate],
	);

	const selectSection = useCallback(
		(key: PatientSectionKey) => {
			patientNav?.onSectionSelect(key);
			setMobileOpen(false);
		},
		[patientNav],
	);

	// Same shape as `selectSection`: fire the action, then close the mobile drawer — otherwise
	// the booking modal opens behind it.
	const bookAppointment = useCallback(() => {
		patientNav?.onBookAppointment?.();
		setMobileOpen(false);
	}, [patientNav]);

	// ── Navigation structure ─────────────────────────────────────

	const isPatientUser = isPatientSession();
	const showCustomerShop = isPatientUser && isCustomerDirectoryMember(user);
	const hasPeptideFeature = !isPatientUser && hasFeature('healthcare.peptide');
	const showPeptideStandalone = !isPatientUser && hasPeptideFeature && !hasFeature('healthcare');
	// Each entry shares its predicate with its route guard in App.tsx, so what is shown is what can be opened.
	const healthcareOn = hasFeature('healthcare');
	const labOn = hasFeature('laboratory');
	const showGeneCodes = canUseGeneCodesWheel();
	const showInventory = hasInventoryAccess();
	const showResearch = canAccessResearch();
	const showHiReviews = canReviewHiAlerts();

	const xoeChatMode = useXoeChatMode();
	const showXoe = canAccessXoe() && (xoeChatMode === "fullpage" || xoeChatMode === "both");

	const { openSettings } = useSettings();
	const hasSettingsTabs = selectableSettingsTabs().length > 0;

	const profileItems: MenuProps["items"] = [
		{ key: "profile", icon: <UserOutlined />, label: "Profile", onClick: () => navigate("/profile") },
		...(hasSettingsTabs
			? [{ key: "app-settings", icon: <SettingOutlined />, label: "Settings", onClick: () => openSettings() }]
			: []),
		{ key: "help", icon: <QuestionOutlined />, label: "Help & Support", onClick: () => navigate("/help") },
		{ key: "security", icon: <SafetyOutlined />, label: "Security", onClick: () => navigate(ENROLMENT_PATH) },
		{ key: "pw", icon: <LockOutlined />, label: "Reset Password", onClick: () => setShowPasswordReset(true) },
		{ type: "divider" },
		{ key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: () => navigate("/logout") },
	];

	const userInitials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();

	const navEntries: NavEntry[] = [
		{
			kind: "item",
			path: "/xoe",
			label: "Xoe",
			icon: <XoeMark size={16} showMonogram={false} animated={false} />,
			visible: showXoe,
			isActive: (p) => p === "/xoe" || p.startsWith("/xoe/"),
		},
		{ kind: "item", path: "/research", label: "Research", icon: <ReadOutlined />, visible: showResearch },
		// Unheaded on purpose: Gene.Codes is not a Laboratory destination, and a lone item under
		// LABORATORY would prop up that heading for a clinic with no lab access (D6).
		{ kind: "item", path: "/gene.codes/wheel", label: "Wheel Builder", icon: <PartitionOutlined />, visible: showGeneCodes },

		// ── Healthcare ────────────────────────────────────────────
		{ kind: "heading", label: "Healthcare" },
		{ kind: "item", path: "/healthcare/appointments", label: "Appointments", icon: <CalendarOutlined />, visible: healthcareOn && canViewAppointments() },
		{ kind: "item", path: "/healthcare/communications", label: "Communications", icon: <MessageOutlined />, visible: healthcareOn && canViewCommunications() },
		{ kind: "item", path: "/healthcare/forms-intray", label: "Forms", icon: <FormOutlined />, visible: healthcareOn && canViewFormsInTray() },
		{ kind: "item", path: "/healthcare/patients", label: "Patients", icon: <UserOutlined />, visible: healthcareOn && canViewPatients() },
		{ kind: "item", path: "/healthcare/treatment-plans", label: "Treatment Plans", icon: <MedicineBoxOutlined />, visible: healthcareOn && canViewTreatmentPlans() },
		{ kind: "item", path: "/healthcare/intray", label: "Result In Tray", icon: <InboxOutlined />, visible: healthcareOn && canViewTestOrders() && hasFeature('healthcare.intray') },
		{ kind: "item", path: "/healthcare/results", label: "Test Orders", icon: <FileTextOutlined />, visible: healthcareOn && canViewTestOrders() && hasFeature('healthcare.results') },
		{ kind: "item", path: "/healthcare/peptide-dashboard", label: "Peptide Plans", icon: <HeartOutlined />, visible: healthcareOn && canViewTreatmentPlans() && hasPeptideFeature },
		{ kind: "item", path: "/healthcare/patients", label: "Patients", icon: <UserOutlined />, visible: showPeptideStandalone && canViewPatients() },
		{ kind: "item", path: "/healthcare/peptide-dashboard", label: "Treatment Plans", icon: <MedicineBoxOutlined />, visible: showPeptideStandalone && canViewTreatmentPlans() },

		// ── Laboratory ────────────────────────────────────────────
		{ kind: "heading", label: "Laboratory" },
		{ kind: "item", path: "/lab/orders", label: "Orders", icon: <ProfileOutlined />, visible: labOn && canViewLabOrders() },
		{ kind: "item", path: "/lab/processing", label: "Processing", icon: <ExperimentOutlined />, visible: labOn && canViewLabProcessing() },
		{ kind: "item", path: "/lab/tracking", label: "Tracking", icon: <NodeIndexOutlined />, visible: labOn && canViewLabTracking() },

		// ── Finance ───────────────────────────────────────────────
		{ kind: "heading", label: "Finance" },
		{ kind: "item", path: "/finance/invoices", label: "Invoices", icon: <FileDoneOutlined />, visible: canRaiseInvoices() },
		{ kind: "item", path: "/finance/payments", label: "Payments", icon: <CreditCardOutlined />, visible: canViewPayments() },
		// { kind: "item", path: "/finance/subscriptions", label: "Subscriptions", icon: <SyncOutlined />, visible: showFinance },

		// ── Inventory ─────────────────────────────────────────────
		{ kind: "heading", label: "Inventory" },
		{ kind: "item", path: "/inventory/products", label: "Products", icon: <ShopOutlined />, visible: showInventory },
		{ kind: "item", path: "/inventory/orders", label: "Inventory Orders", icon: <ShoppingCartOutlined />, visible: showInventory },
		{ kind: "item", path: "/inventory/customers", label: "Customers", icon: <ContactsOutlined />, visible: showInventory },

		// ── Admin ─────────────────────────────────────────────────
		{ kind: "heading", label: "Admin" },
		{ kind: "item", path: "/admin/hi-alerts", label: "HI Reviews", icon: <SafetyCertificateOutlined />, visible: showHiReviews },
	];

	const sectionHasVisibleEntry = (headingIndex: number): boolean => {
		for (let i = headingIndex + 1; i < navEntries.length; i++) {
			const entry = navEntries[i];
			if (entry.kind === "heading") return false;
			if (entry.kind === "item" && entry.visible) return true;
		}
		return false;
	};

	const sidebarClass = ["mg-sidebar", collapsed ? "collapsed" : "", mobileOpen ? "mobile-open" : ""].filter(Boolean).join(" ");

	return (
		<>
			{/* Mobile overlay */}
			{mobileOpen && <div style={{ position: "fixed", inset: 0, zIndex: 99, background: "rgba(0,0,0,0.5)" }} onClick={() => setMobileOpen(false)} />}

			{/* Mobile hamburger — display controlled via CSS media query */}
			<button
				className="mg-mobile-menu-btn"
				onClick={() => setMobileOpen((v) => !v)}
				aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
			>
				<MenuOutlined />
			</button>

			<aside className={sidebarClass} role="navigation">
				{/* Logo */}
				<div className="mg-sidebar-logo">
					{isResolved && !branding.whitelabel ? (
						<span className="mg-sidebar-logo-text">Xoe</span>
					) : (
						<BrandLogo height={26} className="mg-sidebar-brand-logo" />
					)}
					<button className="mg-sidebar-collapse-btn" onClick={() => onCollapse(!collapsed)} title={collapsed ? "Expand" : "Collapse"}>
						{collapsed ? <RightOutlined /> : <LeftOutlined />}
					</button>
				</div>

				{/* Navigation scroll area */}
				<div className="mg-sidebar-scroll">
					{patientNav?.onBookAppointment && (
						<>
							<NavItem
								navKey="__book-appointment__"
								label="Book Appointment"
								icon={<CalendarFilled />}
								isActive={false}
								collapsed={collapsed}
								testId="patient-portal-book-btn"
								onClick={bookAppointment}
								cta
							/>
							<div className="mg-nav-divider" />
						</>
					)}

					<NavItem
						navKey="/"
						label="Dashboard"
						icon={<HomeOutlined />}
						isActive={activePath === "/" || activePath === "/patient"}
						collapsed={collapsed}
						onClick={() => go(patientNav ? "/patient" : "/")}
					/>

					{patientNav && (
						<>
							<div className="mg-nav-divider" />
							{!collapsed && <div className="mg-nav-section-heading">My health</div>}
							{patientNav.sections.map((section) => (
								<NavItem
									key={section.key}
									navKey={section.key}
									label={section.label}
									icon={section.icon}
									badge={patientNav.counts[section.key]}
									isActive={patientNav.activeSectionKey === section.key}
									collapsed={collapsed}
									testId={section.navTestId}
									onClick={() => selectSection(section.key)}
								/>
							))}
						</>
					)}

					{showCustomerShop && (
						<>
							<div className="mg-nav-divider" />
							{!collapsed && <div className="mg-nav-section-heading">Shop</div>}
							<NavItem
								navKey={CUSTOMER_PRODUCTS_PATH}
								label="Products"
								icon={<ShoppingOutlined />}
								isActive={isPathActive(CUSTOMER_PRODUCTS_PATH)(activePath)}
								collapsed={collapsed}
								testId="patient-portal-shop-products"
								onClick={() => go(CUSTOMER_PRODUCTS_PATH)}
							/>
						</>
					)}

					{navEntries.map((entry, i) => {
						if (entry.kind === "heading") {
							if (!sectionHasVisibleEntry(i)) return null;
							return collapsed ? (
								<div key={`heading-${i}`} className="mg-nav-divider" />
							) : (
								<div key={`heading-${i}`} className="mg-nav-section-heading">
									{entry.label}
								</div>
							);
						}

						if (!entry.visible) return null;

						if (entry.kind === "group") {
							return <NavGroup key={entry.group.key} group={entry.group} collapsed={collapsed} activePath={activePath} onNavigate={go} />;
						}

						return (
							<NavItem
								key={`${i}-${entry.path}`}
								navKey={entry.path}
								label={entry.label}
								icon={entry.icon}
								isActive={(entry.isActive ?? isPathActive(entry.path))(activePath)}
								collapsed={collapsed}
								onClick={() => go(entry.path)}
							/>
						);
					})}
				</div>

				{/* Footer */}
				<div className="mg-sidebar-footer">
					<PoweredBy className="mg-sidebar-powered-by" />

					{/* User profile */}
					<Dropdown menu={{ items: profileItems }} trigger={["click"]} placement="topRight">
						<Tooltip title={collapsed ? `${user.first_name} ${user.last_name}` : ""} placement="right">
							<div className="mg-sidebar-user" data-testid="nav-user-menu">
								<Avatar src={user.profile_picture || undefined} size={32} style={{ flexShrink: 0, background: "var(--mg-primary)", fontSize: 12, fontWeight: 600 }}>
									{!user.profile_picture && userInitials}
								</Avatar>
								<div className="mg-sidebar-user-info">
									<div className="mg-sidebar-user-name">
										{user.first_name} {user.last_name}
									</div>
								</div>
								{!collapsed && <DownOutlined className="mg-sidebar-user-chevron" />}
							</div>
						</Tooltip>
					</Dropdown>
				</div>
			</aside>

			<PasswordResetModal open={showPasswordReset} onClose={() => setShowPasswordReset(false)} />
		</>
	);
};

export default Sidebar;
